import { PartitionOutlined, TableOutlined } from '@ant-design/icons';
import { UploadedFile } from '@frontend/data-access';
import { $generateHtmlFromNodes } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Button, Form, Input, Modal, Tooltip } from 'antd';
import getConfig from 'next/config';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import ImageUpload from '../../image-upload/image-upload';
import { INSERT_EXCALIDRAW_COMMAND } from '../../rich-lexical-editor/plugins/ExcalidrawPlugin';
import { INSERT_IMAGE_COMMAND } from '../../rich-lexical-editor/plugins/ImagePlugin';
import { INSERT_TABLE_COMMAND } from './TablePlugin';

type Props = {
  children: ReactNode;
  target: string;
};

const Portal = ({ children, target }: Props) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  const targetElement = document.querySelector(target);
  return targetElement ? createPortal(children, targetElement) : null;
};

interface ToolbarPluginProps {
  target?: string;
  setValue: (value: string) => void;
}

export default function ToolbarPlugin({
  target,
  setValue,
}: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [openImageUploadModal, setOpenImageUploadModal] =
    useState<boolean>(false);
  const [openTableModal, setOpenTableModal] = useState<boolean>(false);
  const [modalKey, setModalKey] = useState<number>(0);
  const { publicRuntimeConfig } = getConfig();
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);

  const handleUploadModal = useCallback(() => {
    if (!openImageUploadModal) {
      // When opening modal, increment key to reset ImageUpload component
      setModalKey((prev) => prev + 1);
    }
    setOpenImageUploadModal(!openImageUploadModal);
  }, [openImageUploadModal]);

  const onImageUploadSuccess = useCallback(
    (params: UploadedFile) => {
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
        altText: '',
        src: params.url || '',
      });
      editor.update(() => {
        const html = $generateHtmlFromNodes(editor, null);
        setValue(html);
      });
      handleUploadModal();
    },
    [editor, setValue, handleUploadModal]
  );

  const onLoading = useCallback((loading: boolean) => {
    setLoading(loading);
  }, []);

  const handleTableModal = useCallback(() => {
    setOpenTableModal(!openTableModal);
  }, [openTableModal]);

  const onTableInsert = useCallback(
    (values: { rows: string; columns: string }) => {
      editor.dispatchCommand(INSERT_TABLE_COMMAND, {
        rows: values.rows,
        columns: values.columns,
        includeHeaders: false,
      });
      editor.update(() => {
        const html = $generateHtmlFromNodes(editor, null);
        setValue(html);
      });
      handleTableModal();
    },
    [editor, setValue, handleTableModal]
  );

  const handleExcalidrawInsert = useCallback(() => {
    editor.dispatchCommand(INSERT_EXCALIDRAW_COMMAND, undefined);
  }, [editor]);

  // Memoize modal footer to prevent recreation
  const uploadModalFooter = useMemo(
    () => [
      <Button
        key="ok"
        type="primary"
        onClick={handleUploadModal}
        loading={loading}
      >
        {t('ok')}
      </Button>,
    ],
    [handleUploadModal, loading, t]
  );

  if (!target) return null;

  return (
    <div>
      <Portal target={`#${target}`}>
        <div className="flex items-center gap-2">
          <Tooltip title={t('add_media')}>
            <button
              onClick={handleUploadModal}
              className="toolbar-item spaced bg-gray-200 hover:bg-gray-300 px-2 py-1 text-xs rounded-md flex items-center gap-2"
              aria-label="Add Media"
              type="button"
            >
              <Image
                src={`${publicRuntimeConfig.staticFolder}/svg/file-image-upload.svg`}
                alt="Add Media"
                width={16}
                height={16}
              />
            </button>
          </Tooltip>
          <Tooltip title={t('add_table')}>
            <button
              onClick={handleTableModal}
              className="toolbar-item spaced bg-gray-200 hover:bg-gray-300 px-2 py-1 text-xs rounded-md flex items-center gap-2"
              aria-label="Insert Table"
              type="button"
            >
              <TableOutlined style={{ fontSize: '16px' }} />
            </button>
          </Tooltip>
          <Tooltip title={t('add_diagram')}>
            <button
              onClick={handleExcalidrawInsert}
              className="toolbar-item spaced bg-gray-200 hover:bg-gray-300 px-2 py-1 text-xs rounded-md flex items-center gap-2"
              aria-label="Insert Diagram"
              type="button"
            >
              <PartitionOutlined style={{ fontSize: '16px' }} />
            </button>
          </Tooltip>
        </div>
      </Portal>

      <Modal
        title={t('upload_image_modal_title')}
        open={openImageUploadModal}
        closable={!loading}
        onOk={handleUploadModal}
        onCancel={handleUploadModal}
        footer={uploadModalFooter}
      >
        <ImageUpload
          key={modalKey}
          title={t('upload_image_title')}
          description={t('upload_image_default_support_description')}
          compact={false}
          enableCrop={false}
          maxHeight={400}
          onUploadSuccess={onImageUploadSuccess}
          onLoading={onLoading}
        />
      </Modal>

      <Modal
        title={t('insert_table')}
        open={openTableModal}
        onCancel={handleTableModal}
        footer={null}
      >
        <Form
          name="table-insert"
          onFinish={onTableInsert}
          initialValues={{ rows: '3', columns: '3' }}
          layout="vertical"
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Columns"
              name="columns"
              rules={[
                { required: true, message: 'Please input number of columns' },
              ]}
            >
              <Input type="number" min={1} max={20} />
            </Form.Item>
            <Form.Item
              label="Rows"
              name="rows"
              rules={[
                { required: true, message: 'Please input number of rows' },
              ]}
            >
              <Input type="number" min={1} max={100} />
            </Form.Item>
          </div>
          <div className="flex justify-end">
            <Form.Item noStyle>
              <Button type="primary" htmlType="submit">
                {t('insert_table')}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
